import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import { prepareImageForVerification } from "../utils/imageUtils";

const API_URL = process.env.REACT_APP_API_URL;

interface LinkData {
  id: number;
  token: string;
  branch_id?: string;
  merchant_id: string;
  verification_type: string;
}

const VerificationLink: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { showToast } = useToast();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [linkData, setLinkData] = useState<LinkData | null>(null);
  const [ghanaCardNumber, setGhanaCardNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [captured, setCaptured] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showCamera, setShowCamera] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);

  const fetchLinkData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/links/${token}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === "Link is inactive") {
          setIsVerified(true);
          setError(
            "This verification link has already been used and is no longer active."
          );
        } else {
          throw new Error(errorData.error || "Link not found or expired");
        }
      } else {
        const data = await response.json();
        setLinkData(data.link);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLinkData();
    return () => {
      stopCamera();
    };
  }, [fetchLinkData]);

  const startCamera = async () => {
    try {
      stopCamera();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user",
        },
      });

      if (videoRef.current) {
        const video = videoRef.current;
        video.srcObject = stream;

        const playVideo = () => {
          if (video.readyState >= 2) {
            video.play().catch((err) => {
              console.log("Play() prevented:", err.message);
            });
          } else {
            video.addEventListener(
              "loadedmetadata",
              () => {
                video.play().catch((err) => {
                  console.log("Play() prevented:", err.message);
                });
              },
              { once: true }
            );
          }
        };

        setTimeout(playVideo, 50);
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setError(
        "Camera access denied. Please allow camera access and refresh the page."
      );
    }
  };

  const stopCamera = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      if (video.srcObject) {
        const tracks = (video.srcObject as MediaStream).getTracks();
        tracks.forEach((track) => track.stop());
      }
      video.srcObject = null;
      video.pause();
    }
  };

  const validateGhanaCardNumber = (cardNumber: string): boolean => {
    const ghanaCardRegex = /^GHA-\d{9}-\d$/;
    return ghanaCardRegex.test(cardNumber);
  };

  const validateDateOfBirth = (dob: string): boolean => {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dob)) return false;

    const date = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - date.getFullYear();

    return age >= 18 && age <= 120;
  };

  const formatGhanaCardNumber = (text: string): string => {
    const cleaned = text.replace(/[^A-Za-z0-9]/g, "");

    if (cleaned.length <= 3) {
      return cleaned.toUpperCase();
    } else if (cleaned.length <= 12) {
      return `GHA-${cleaned.slice(3, 12)}`;
    } else {
      return `GHA-${cleaned.slice(3, 12)}-${cleaned.slice(12, 13)}`;
    }
  };

  const formatDateOfBirth = (text: string): string => {
    // Remove all non-digit characters
    const cleaned = text.replace(/\D/g, "");

    // Format as YYYY-MM-DD
    if (cleaned.length <= 4) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
    } else {
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 6)}-${cleaned.slice(
        6,
        8
      )}`;
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!ghanaCardNumber.trim()) {
      setError("Please enter your Ghana Card number");
      return;
    }

    if (!dateOfBirth.trim()) {
      setError("Please enter your date of birth");
      return;
    }

    if (!validateGhanaCardNumber(ghanaCardNumber)) {
      setError(
        "Please enter a valid Ghana Card number (Format: GHA-000000000-0)"
      );
      return;
    }

    if (!validateDateOfBirth(dateOfBirth)) {
      setError(
        "Please enter a valid date of birth (YYYY-MM-DD) and ensure you are 18+ years old"
      );
      return;
    }

    setShowCamera(true);
    await startCamera();
  };

  const capturePhoto = async () => {
    if (!videoRef.current || captured || isVerifying || !linkData) return;

    setCaptured(true);
    setError(null);
    setIsVerifying(true);

    try {
      const base64Image = await prepareImageForVerification(videoRef.current);
      stopCamera();

      const response = await fetch(`${API_URL}/verification/${token}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ghana_card_number: ghanaCardNumber,
          date_of_birth: dateOfBirth,
          image: base64Image,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Verification failed");
      }

      const result = await response.json();
      setVerificationResult(result);
      setIsVerifying(false);

      if (result.success && result.verified) {
        setIsVerified(true);
        // Use image from verification API response
        if (result.image) {
          // If image is already a data URL, use it directly, otherwise add the prefix
          const imageUrl = result.image.startsWith("data:")
            ? result.image
            : `data:image/png;base64,${result.image}`;
          setCapturedImage(imageUrl);
        }
        showToast("Verification Successful!", "success", 5000);
      } else {
        let errorMessage = result.message || "Verification failed";

        if (result.code === "03") {
          errorMessage =
            "This person is on the NIA watch list. They need to visit the NIA office to resolve registration issues.";
        } else if (result.code === "02") {
          errorMessage =
            "Invalid data provided. Please check your input and try again.";
        } else if (result.code === "01") {
          errorMessage =
            "Face verification unsuccessful. Please ensure good lighting, face is clearly visible, and you are looking directly at the camera.";
        } else if (result.code === "11") {
          errorMessage =
            "Face too small in the image. Please move closer to the camera and ensure your face fills most of the frame.";
        } else if (result.code === "04") {
          errorMessage = "Internal server error. Please try again later.";
        }

        showToast(errorMessage, "error", 6000);
      }
    } catch (err: any) {
      console.error("Verification error:", err);
      const errorMessage =
        err.message || "Failed to verify face. Please try again.";
      setError(errorMessage);
      showToast(errorMessage, "error", 5000);
      setIsVerifying(false);
      setCaptured(false);
      startCamera();
    }
  };

  const handleRetry = () => {
    setVerificationResult(null);
    setCaptured(false);
    setError(null);
    setIsVerifying(false);
    startCamera();
  };

  const handleBackToForm = () => {
    setShowCamera(false);
    setCaptured(false);
    setVerificationResult(null);
    setError(null);
    setIsVerifying(false);
    stopCamera();
  };

  if (loading) {
    return (
      <div className="container">
        <div className="header">
          <h1 className="title">Loading...</h1>
        </div>
      </div>
    );
  }

  if (error && !linkData) {
    return (
      <div className="container">
        <div className="header">
          <h1 className="title">Error</h1>
          <p className="subtitle">{error}</p>
        </div>
      </div>
    );
  }

  if (isVerified) {
    return (
      <div className="container">
        <div className="header">
          <h1 className="title" style={{ color: "#004a9c" }}>
            ✓ Verification Successful
          </h1>
          <p className="subtitle">
            Your identity has been successfully verified.
          </p>
        </div>
        <div className="form" style={{ textAlign: "center" }}>
          {capturedImage && (
            <div style={{ marginBottom: "24px" }}>
              <img
                src={capturedImage}
                alt="Verified customer"
                style={{
                  maxWidth: "400px",
                  maxHeight: "400px",
                  width: "100%",
                  height: "auto",
                  border: "2px solid #004a9c",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
              />
            </div>
          )}
          <p style={{ color: "#000000", fontSize: "0.9rem" }}>
            This verification link has been used and is no longer active.
          </p>
        </div>
      </div>
    );
  }

  if (!showCamera && !captured && !isVerifying) {
    return (
      <div className="container">
        <div className="header">
          <img
            src="/nib-logo-black.webp"
            alt="NIB Logo"
            className="logo"
            style={{ height: "80px", width: "auto", marginBottom: "20px" }}
          />
          <h1 style={{ fontSize: "30px" }} className="title">
            Online ID verification
          </h1>
          <p style={{ fontSize: "16px" }} className="subtitle">
            Please enter your Ghana Card details to proceed with face
            verification
          </p>
        </div>

        <form className="form" onSubmit={handleFormSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="input-container">
            <label className="label" htmlFor="ghanaCard">
              Ghana Card Number
            </label>
            <input
              id="ghanaCard"
              type="text"
              className="input"
              value={ghanaCardNumber}
              onChange={(e) =>
                setGhanaCardNumber(formatGhanaCardNumber(e.target.value))
              }
              placeholder="GHA-000000000-0"
              maxLength={14}
              autoComplete="off"
            />
          </div>

          <div className="input-container">
            <label className="label" htmlFor="dateOfBirth">
              Date of Birth
            </label>
            <input
              id="dateOfBirth"
              type="text"
              className="input"
              value={dateOfBirth}
              onChange={(e) =>
                setDateOfBirth(formatDateOfBirth(e.target.value))
              }
              placeholder="YYYY-MM-DD"
              maxLength={10}
              autoComplete="off"
            />
          </div>

          <button type="submit" className="verify-button">
            Continue to Camera
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="camera-container">
      <div className="camera-header">
        <h2>Face Verification</h2>
        <p>Ghana Card: {ghanaCardNumber}</p>
        <p>
          Type:{" "}
          {linkData?.verification_type === "yesno"
            ? "YES/NO Verification"
            : "KYC Verification"}
        </p>
      </div>

      <div className="camera-content">
        {isVerifying && (
          <div className="verification-overlay">
            <div className="verification-spinner"></div>
            <div className="verification-message">
              Verifying your identity...
            </div>
            <div className="verification-submessage">
              Please wait, this may take a few seconds
            </div>
          </div>
        )}

        <div
          className="video-container"
          style={{ opacity: isVerifying ? 0.5 : 1 }}
        >
          <video ref={videoRef} className="video" autoPlay muted playsInline />
        </div>

        {error && !isVerifying && <div className="status error">{error}</div>}

        {verificationResult && !isVerifying && (
          <div
            className={`status ${
              verificationResult.verified ? "success" : "error"
            }`}
          >
            {verificationResult.verified
              ? "Verification Successful"
              : `Verification Failed: ${verificationResult.message}`}
          </div>
        )}

        <div className="controls">
          {!captured && !isVerifying && (
            <>
              <button
                className="control-button back-button"
                onClick={handleBackToForm}
              >
                Back to Form
              </button>
              <button
                className="control-button capture-button"
                onClick={capturePhoto}
              >
                Capture Photo
              </button>
            </>
          )}

          {isVerifying && (
            <div style={{ color: "white", padding: "12px" }}>
              Verifying... Please wait
            </div>
          )}

          {verificationResult && !isVerifying && !isVerified && (
            <>
              {!verificationResult.verified && (
                <button
                  className="control-button capture-button"
                  onClick={handleRetry}
                >
                  Retry Capture
                </button>
              )}
              <button
                className="control-button back-button"
                onClick={handleBackToForm}
              >
                Back to Form
              </button>
            </>
          )}
        </div>

        <div className="instructions-container">
          <button
            className="instructions-toggle"
            onClick={() => setShowInstructions(!showInstructions)}
            aria-label="Toggle instructions"
          >
            {showInstructions ? "Hide Instructions" : "Show Instructions"}
          </button>
          {showInstructions && (
            <div className="instructions">
              <h3>Instructions</h3>
              <p>1. Position your face in the center of the camera</p>
              <p>2. Move close enough so your face fills most of the frame</p>
              <p>3. Ensure good lighting and look directly at the camera</p>
              <p>4. Click "Capture Photo" to take your picture</p>
              <p>5. Keep your face steady when capturing</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationLink;
